import classNames from 'classnames'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { ZStack } from '~/components/elements/ZStack'
import {
  findByPath,
  formatNineSegment,
  type NineNumber,
  NumFormat,
} from '~/utils'

import { useSurvey } from '../../hooks/useSurvey'
import { useTabulationData } from '../../hooks/useTabulationQuery'
import { type InputValues } from '../../utils'

import { Circle } from './Circle'
import styles from './NineNumberCell.css'

export type NineNumberCellProps = {
  index: number
  nineNumber: NineNumber
}

export const NineNumberCell = ({ index, nineNumber }: NineNumberCellProps) => {
  const { t } = useTranslation()
  const namePrefix = `strategies.${index}` as const
  const { watch } = useFormContext<InputValues>()
  const [showArrow, segmentRoutes, dataDisplayType] = watch([
    `${namePrefix}.showArrow`,
    `${namePrefix}.segmentRoutes`,
    'dataDisplayType',
  ])

  const label = React.useMemo(
    () => formatNineSegment(nineNumber, '%n %t1', t),
    [nineNumber, t],
  )

  const tabulationData = useTabulationData()
  const survey = useSurvey()

  const value = React.useMemo(() => {
    const data = R.pipe(tabulationData, findByPath('nineNumber', nineNumber))
    const count = data?.count ?? NaN
    const ratio = data?.ratio ?? NaN

    const tamNumber = survey?.metadata?.tamNumber ?? 0
    const numberOfTotalAnswers = survey?.numberOfTotalAnswers ?? NaN
    const tamRatio = tamNumber === 0 ? 1 : tamNumber / numberOfTotalAnswers

    return NumFormat.forCount(count, ratio, tamRatio, dataDisplayType)
  }, [
    dataDisplayType,
    nineNumber,
    survey?.metadata?.tamNumber,
    survey?.numberOfTotalAnswers,
    tabulationData,
  ])

  return (
    <ZStack
      className={classNames(
        styles.root,
        styles[`segment-${nineNumber}`],
        showArrow &&
          segmentRoutes.some((segmentRoute) =>
            R.values(segmentRoute).includes(nineNumber),
          ) &&
          styles.active,
      )}
    >
      <VStack
        align="center"
        backgroundColor="transparent"
        justify="center"
        spacing={0}
      >
        <Circle nineNumber={nineNumber} />
      </VStack>

      <VStack
        align="center"
        backgroundColor="transparent"
        justify="space-between"
        spacing={0}
      >
        <Typography variant="small">{label}</Typography>

        <Typography lineHeight={2.5} variant="small">
          {value}
        </Typography>
      </VStack>
    </ZStack>
  )
}
